import type { AgentSummary, FactoryLayout, HoldReason, MergeQueueItem, Run, Task, TaskSpend } from "../types";

/** The tracker issue key, or null for work orders created by hand. */
export function issueKey(task: Task): string | null {
  return task.issueIdentifier && task.issueIdentifier !== "MANUAL" ? task.issueIdentifier : null;
}

export type TaskMood =
  | "working"
  | "waiting"
  | "blocked"
  | "failed"
  | "input"
  | "approve"
  | "human"
  | "merging"
  | "idle";

export const MOOD_TEXT: Record<TaskMood, string> = {
  working: "Working",
  waiting: "Queued for the station",
  blocked: "Blocked: needs a tool or credentials",
  failed: "Failed",
  input: "Waiting for your input",
  approve: "Waiting for merge approval",
  human: "Waiting on a person: reply and resume",
  merging: "Merging",
  idle: "Between stations",
};

/** What an in-flight work order is doing right now, from its current run and merge ticket. */
export function taskMood(task: Task, run: Run | undefined, merge: MergeQueueItem | undefined): TaskMood {
  if (run?.status === "failed" || run?.status === "expired") return "failed";
  if (run?.status === "blocked" || task.status === "blocked") return "blocked";
  if (run?.status === "waiting_for_input") return "input";
  if (task.status === "waiting_for_human") return "human";
  if (merge?.status === "merging") return "merging";
  if (task.status === "waiting_for_approval" || needsApproval(merge)) return "approve";
  if (!run || run.status === "queued") return "waiting";
  if (run.status === "running" || run.status === "launching") return "working";
  return "idle";
}

/**
 * What a work order has spent across every station run, the same totals `/tasks` sums in SQL. A run
 * still going counts up to `now`.
 */
export function runSpend(
  runs: ReadonlyArray<Pick<Run, "usage" | "startedAt" | "finishedAt">>,
  now = Date.now(),
): TaskSpend {
  let costUsd: number | null = null;
  let totalTokens: number | null = null;
  let unbilled = 0;
  let agentMs: number | null = null;
  for (const run of runs) {
    const cost = run.usage?.costUsd;
    const tokens = run.usage?.totalTokens;
    if (cost !== undefined) costUsd = (costUsd ?? 0) + cost;
    if (tokens !== undefined) totalTokens = (totalTokens ?? 0) + tokens;
    if (cost === undefined && tokens !== undefined) unbilled += 1;
    if (run.startedAt) {
      const end = run.finishedAt ? Date.parse(run.finishedAt) : now;
      agentMs = (agentMs ?? 0) + Math.max(0, end - Date.parse(run.startedAt));
    }
  }
  return { costUsd, totalTokens, unbilled, agentMs };
}

/**
 * When a station last moved the work order: the latest time a run was queued, started or finished.
 * Run `updatedAt` is left out because engine bookkeeping bumps it without anything visible changing.
 */
export function lastStationUpdate(
  task: Pick<Task, "createdAt">,
  runs: ReadonlyArray<Pick<Run, "createdAt" | "startedAt" | "finishedAt">>,
): string {
  let latest = task.createdAt;
  for (const run of runs) {
    for (const at of [run.createdAt, run.startedAt, run.finishedAt]) {
      if (at && Date.parse(at) > Date.parse(latest)) latest = at;
    }
  }
  return latest;
}

/** Each station that has run on a work order with its run count, in the order the stations first ran. */
export function runStations(
  runs: ReadonlyArray<Pick<Run, "agentId" | "createdAt">>,
): Array<{ agentId: string; runs: number }> {
  const counts = new Map<string, number>();
  for (const run of [...runs].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))) {
    counts.set(run.agentId, (counts.get(run.agentId) ?? 0) + 1);
  }
  return [...counts].map(([agentId, count]) => ({ agentId, runs: count }));
}

export function needsApproval(item: MergeQueueItem | undefined): boolean {
  return Boolean(item?.requiresApproval && !item.approvedAt && ["waiting", "ready"].includes(item.status));
}

const CLOSED_MERGE = new Set<MergeQueueItem["status"]>(["merged", "cancelled", "failed", "merging"]);

/** GitHub still has this pull request in draft. */
export function isDraftPullRequest(item: MergeQueueItem): boolean {
  return (
    item.checks.draft === true ||
    item.blockers.some((blocker) => blocker.toLowerCase() === "pull request is a draft")
  );
}

/** First letter uppercased, so a stored blocker reads as a sentence. */
export function asSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** The action the merge queue can take on this pull request right now. */
export function mergeAction(item: MergeQueueItem): "ready" | "ship" | null {
  if (CLOSED_MERGE.has(item.status)) return null;
  if (isDraftPullRequest(item)) return "ready";
  if (item.status === "held") return null;
  return "ship";
}

export const ACTIVE_TASK: ReadonlySet<Task["status"]> = new Set([
  "open",
  "blocked",
  "waiting_for_approval",
  "waiting_for_human",
]);

export function isOpenTask(task: Task): boolean {
  return ACTIVE_TASK.has(task.status);
}

/** Why the engine held the work order for a person, when it did: a spent loop budget or a Fixer that pushed nothing. */
export function holdReason(task: Task): HoldReason | null {
  if (task.status !== "waiting_for_human") return null;
  const reason = task.meta.holdReason as HoldReason | undefined;
  return reason?.kind === "loop_budget" || reason?.kind === "no_progress" ? reason : null;
}

export function isDetachedAgent(layout: FactoryLayout | undefined, agentId: string | null): boolean {
  if (!layout || !agentId) return false;
  return layout.sections.some((s) => s.detached && s.agents.some((a) => a.id === agentId));
}

/** A run of a detached specialist rather than a work order on the line. */
export function isDetachedTask(task: Task, layout: FactoryLayout | undefined): boolean {
  return isDetachedAgent(layout, task.currentAgentId);
}

export function describeNext(
  next: string,
  agents: Map<string, AgentSummary>,
  pipeline = "Next station on the lane",
): string {
  if (next === "pipeline") return pipeline;
  if (next === "stop") return "Stop the line";
  return agents.get(next)?.name ?? next;
}

/** Where "pipeline" leads from one station: the next intake station, the lane intake picks, or the next station on each lane. */
export function describePipeline(agentId: string, layout: FactoryLayout, agents: Map<string, AgentSummary>) {
  const name = (id: string) => agents.get(id)?.name ?? id;
  const intakeIndex = layout.intake.indexOf(agentId);
  if (intakeIndex !== -1) {
    const next = layout.intake[intakeIndex + 1];
    return next ? name(next) : "First station of the lane it picks";
  }
  const steps = Object.values(layout.lanes).flatMap((lane) => {
    const index = lane.stations.indexOf(agentId);
    if (index === -1) return [];
    const next = lane.stations[index + 1];
    return [`${lane.name}: ${next ? name(next) : "end of the lane"}`];
  });
  return steps.length ? steps.join(" · ") : "Stop";
}

export function triggerText(trigger: Record<string, unknown> & { type: string }): string {
  if (trigger.type === "tracker.state") return `Issue enters ${String(trigger.state)}`;
  if (trigger.type === "tracker.label") return `Label ${String(trigger.label)} is added`;
  if (trigger.type === "tracker.delegated") return "Issue is delegated to Skynet";
  if (trigger.type === "handoff") return "Hand-off from the previous station";
  if (trigger.type === "manual") return "Started by hand from the dashboard";
  return trigger.type;
}
